import { Component, Input, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CreateContactButtonComponent } from '../../address-books/create-contact-button/create-contact-button.component';
import { CreateShipmentButtonComponent } from '../../shipment/create-shipment-button/create-shipment-button.component';
import { AsyncPipe } from '@angular/common';
import { selectRole } from '../../../store/profile/profile.state';
import { RoleEnum } from '../../../enum/role.enum';
import { selectShipmentEditorShipment } from '../../../store/shipment-editor/shipment-editor.state';
import { ShipmentService } from '../../../data/shipment.service';
import { Router } from '@angular/router';
import { contactEditorNew } from '../../../store/contact-editor/contact-editor.actions';
import { commodityEditorNew } from '../../../store/commodity-editor/commodity-editor.actions';
import { packageEditorNew } from '../../../store/package-editor/package-editor.actions';
import { MatFabButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { IconComponent } from '../../shared/icon/icon.component';
import { shipmentEditorLoadSuccess } from '../../../store/shipment-editor/shipment-editor.actions';

@Component({
    selector: 'app-create-entity-button',
    templateUrl: './create-entity-button.component.html',
    styleUrls: [ './create-entity-button.component.scss' ],
    imports: [ CreateShipmentButtonComponent, CreateContactButtonComponent, AsyncPipe, MatFabButton, MatMenu, MatMenuItem, MatMenuTrigger, IconComponent ]
})
export class CreateEntityButtonComponent implements OnInit {

    @Input()
    public placement: 'rail-top' | 'window-bottom-right' = 'rail-top';

    creating = signal(false);

    constructor(private store: Store<AppState>, private shipmentService: ShipmentService, private router: Router) {
    }

    displayCreateShipment$ = of(true);
    displayCreateContact$ = this.store.select(selectRole(RoleEnum.EDIT_CONTACTS));
    displayCreateLibraryTemplate$: Observable<boolean> = this.store.select(selectRole(RoleEnum.PACKAGE_LIBRARY));
    displayDuplicateShipment$: Observable<boolean> = this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => !!shipment.id)
    );
    displayReturnShipment$: Observable<boolean> = this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => !!shipment.id)
    );

    ngOnInit(): void {
    }

    createShipment(): void {
        if (this.creating()) {
            return;
        }

        this.creating.set(true);

        this.shipmentService.create().pipe(take(1)).subscribe({
            next: (shipment) => {
                // 1) Put the new shipment straight into the editor state
                this.store.dispatch(shipmentEditorLoadSuccess({ shipment }));

                // 2) Navigate directly to the editor route for the new shipment
                this.router.navigate(
                    [ 'shipment', shipment.id, 'edit' ],
                    { replaceUrl: true }  // optional, but usually nicer
                ).then();
            },
            error: () => {
                this.creating.set(false);
            },
            complete: () => {
                this.creating.set(false);
            }
        });
    }

    createContact(): void {
        this.store.dispatch(contactEditorNew());
    }

    createCommodityTemplate(): void {
        this.store.dispatch(commodityEditorNew());
    }

    createPackageTemplate(): void {
        this.store.dispatch(packageEditorNew());
    }

    duplicateShipment(): void {
        if (!this.creating()) {
            this.creating.set(true);
            this.store.select(selectShipmentEditorShipment).pipe(
                switchMap(shipment => this.shipmentService.duplicate(shipment)),
                take(1)
            ).subscribe({
                next: (shipment) => this.router.navigate([ 'shipment', shipment.id ]).then(),
                complete: () => this.creating.set(false)
            });
        }
    }

    returnShipment(): void {
        if (!this.creating()) {
            this.creating.set(true);
            this.store.select(selectShipmentEditorShipment).pipe(
                switchMap(shipment => this.shipmentService.return(shipment)),
                take(1)
            ).subscribe({
                next: (shipment) => this.router.navigate([ 'shipment', shipment.id ]).then(),
                complete: () => this.creating.set(false)
            });
        }
    }
}
